import React, { lazy } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import AccountSettings from "./Pages/Account/AccountSettings";
import Contactus from "./Pages/Contact us/Contactus";
import ForgotPassword from "./Pages/ForgotPassword";
import HelpingPageLayout from "./Pages/HelpingPage/HelpingPageLayout";
import FontNewUser from "./Pages/NewUser/NewUserComponent/FontNewUser";
import LayoutNewUser from "./Pages/NewUser/NewUserComponent/LayoutNewUser";
import PrivacyPolicy from "./Pages/Privacy/PrivacyPolicy";
import MoreInfoPage from "./Pages/ProjectV2/Render/MoreInfoPage";
import RenderLibrary from "./Pages/ProjectV2/Render/RenderLibrary/RenderLibrary";
import NewSignup from "./Pages/Register/NewSignup";
import Term_and_condition from "./Pages/Terms & Conditions/Term_and_condition";
import AllPlansList from "./Pages/UserPlan/AllPlansList";
import PaymentsDetails from "./Pages/UserPlan/PaymentsDetails";
import { Dashboard, NewLogin, ProjectDashboard } from "./Pages/index";
import { logOutBroadCast } from "./Pages/utils/global";
import Success from "./Pages/UserPlan/Success";
import OnSubscriptionPlanList from "./Pages/UserPlan/OnSubscriptionPlanList";
import SubscriptionSuccess from "./Pages/UserPlan/SubscriptionSuccess";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAuthStorage,
  getToken,
  getTimeUntilTokenExpiry,
  isAuthenticated,
  isTokenExpired,
} from "./utils/auth";
import { handleSessionExpired } from "./Redux/actions/commonActtion";

const SurveyForm = lazy(() => import("./Pages/NewUser/SurveyForm"));
const MAX_TIMEOUT_MS = 2147483647;

const hasUserFilled = () =>
  JSON.parse(localStorage.getItem("userFilledSurvey"));

function useSessionGuard() {
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = React.useState(() =>
    isAuthenticated(),
  );

  React.useEffect(() => {
    let sessionExpiryTimeoutId = null;

    const clearExpiryTimer = () => {
      if (sessionExpiryTimeoutId !== null) {
        window.clearTimeout(sessionExpiryTimeoutId);
        sessionExpiryTimeoutId = null;
      }
    };

    const expireSession = () => {
      clearExpiryTimer();
      clearAuthStorage();
      dispatch(handleSessionExpired());
      setAuthenticated(false);
    };

    const syncSessionState = () => {
      const tokenInStorage = localStorage.getItem("authToken");
      const token = getToken();

      if (!tokenInStorage) {
        clearExpiryTimer();
        setAuthenticated(false);
        return;
      }

      if (!token) {
        expireSession();
        return;
      }

      if (isTokenExpired(token)) {
        expireSession();
        return;
      }

      setAuthenticated(true);
      clearExpiryTimer();

      const sessionRemainingMs = getTimeUntilTokenExpiry(token);
      if (sessionRemainingMs <= 0) {
        expireSession();
        return;
      }

      const timeoutDelay = Math.min(sessionRemainingMs, MAX_TIMEOUT_MS);
      sessionExpiryTimeoutId = window.setTimeout(
        sessionRemainingMs > MAX_TIMEOUT_MS ? syncSessionState : expireSession,
        timeoutDelay,
      );
    };

    const handleStorageChange = (event) => {
      if (!event.key || event.key === "authToken") {
        syncSessionState();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncSessionState();
      }
    };

    syncSessionState();
    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearExpiryTimer();
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  return authenticated;
}

function PrivateOutlet() {
  const authenticated = useSessionGuard();
  return authenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

function PublicOutlet() {
  const authenticated = useSessionGuard();
  return !authenticated ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

function SurveyOutlet() {
  return hasUserFilled() === true ? <Navigate to="/dashboard" /> : <Outlet />;
}

function ProtectedCardRoute() {
  const isCardAdded = useSelector(
    (state) => state?.commonReducer?.userData?.[0]?.isCardAdded,
  );
  const isExpired = useSelector(
    (state) => state?.commonReducer?.currentPlan?.isExpired,
  );

  return isCardAdded && !isExpired ? (
    <Outlet />
  ) : (
    <Navigate to="/plans" replace />
  );
}

export default function Router() {
  React.useEffect(() => {
    logOutBroadCast();
  }, []);

  return (
    <div className="DIvRoute w-full h-full">
      <Routes>
        {/* ✅ Private Routes */}
        <Route path="/" element={<PrivateOutlet />}>
          {/* ---------------- Dashboard (Protected by card) ---------------- */}
          <Route element={<ProtectedCardRoute />}>
            <Route exact path="dashboard" element={<Dashboard />} />
            <Route
              exact
              path="dashboard/myrenders"
              element={<RenderLibrary />}
            />
            <Route
              exact
              path="dashboard/card/:projectId"
              element={<ProjectDashboard />}
            />
            <Route
              exact
              path="dashboard/scroll/:projectId"
              element={<ProjectDashboard />}
            />
            <Route path="/dashboard/new-user" element={<Dashboard />} />
            <Route
              exact
              path="/dashboard/projectInstructions/LayoutNewUser"
              element={<LayoutNewUser />}
            />
            <Route
              exact
              path="/dashboard/projectInstructions/FontNewUser"
              element={<FontNewUser />}
            />
          </Route>

          {/* ---------------- Non-card protected routes ---------------- */}
          <Route exact path="account-settings" element={<AccountSettings />} />
          <Route path="/more_info" element={<MoreInfoPage />} />
          <Route exact path="user-plans" element={<AllPlansList />} />
          <Route exact path="plans" element={<OnSubscriptionPlanList />} />
          <Route
            exact
            path="subscription-success"
            element={<SubscriptionSuccess />}
          />
          <Route
            exact
            path="user-plan/plan&payment-details"
            element={<PaymentsDetails />}
          />
          <Route path="/paymentsuccess" element={<Success />} />
          <Route path="/help/" element={<HelpingPageLayout />} />
          <Route path="/help/:id" element={<HelpingPageLayout />} />

          {/* Survey */}
          <Route path="/" element={<SurveyOutlet />}>
            <Route path="/new_user_survey" element={<SurveyForm />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="dashboard" />} />
          <Route path="*" element={<Navigate to="dashboard" />} />
        </Route>

        {/* ✅ Public Routes */}
        <Route path="/" element={<PublicOutlet />}>
          <Route exact index path="/" element={<NewLogin />} />
          <Route exact index path="/login" element={<NewLogin />} />
          <Route exact path="/register" element={<NewSignup />} />
          <Route exact path="/register" element={<NewSignup />} />
          <Route
            exact
            path="/register/terms"
            element={<Term_and_condition />}
          />
          <Route exact path="/contactus" element={<Contactus />} />
          <Route exact path="/forgotpassword" element={<ForgotPassword />} />
          <Route exact path="/PrivacyPolicy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Route>
      </Routes>
    </div>
  );
}
